import { defineStore } from 'pinia'
import type { INavigation, INavigationHeader, ISecondaryNavigationItem } from '@/generated-api'
import { mapOldIconToNew } from '@/application/helpers/IconMapper'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../general-stores/app'

interface INavigationOptions {
  checkLinkExistInNavigation: boolean
  useNavigationItemInModern: boolean
  navItem: INavigationHeader | INavigation | undefined
}

export const useNavigationStore = defineStore('navigation', () => {
  const appStore = useAppStore()
  const { siteContent } = storeToRefs(appStore)

  const navigation = ref<INavigationHeader[]>([])
  const secondaryNavigation = ref<ISecondaryNavigationItem[]>([])
  const currentNavigationUrl = ref<string>('')
  const navigationIsOpen = ref<boolean>(false)
  const isSignInVisible = ref<boolean>(true)
  const isNavItemInMenu = ref<boolean>(false)
  const isNavigating = ref<boolean>(false)

  const setNavigation = (nav: INavigationHeader[]) => {
    navigation.value = nav
    mapIcons()
  }

  const setSignInVisibility = (visible: boolean) => {
    if (isSignInVisible.value !== visible) {
      isSignInVisible.value = visible
    }
  }

  const setCurrentNavigationUrl = (navigation: string) => {
    currentNavigationUrl.value = navigation
  }

  const setNavigationIsOpen = (open: boolean) => {
    navigationIsOpen.value = open
  }

  const mapIcons = () => {
    // Map from old angular-js icons to new fontawesome icons
    const updatedNavigation = navigation.value.map((item) => {
      if (item.icon) {
        return { ...item, icon: mapOldIconToNew(item.icon) || item.icon }
      }
      return item
    })

    navigation.value = updatedNavigation
  }

  const resetNavigationStore = () => {
    setNavigationIsOpen(false)
    setNavigation([])
    setSignInVisibility(true)
    setCurrentNavigationUrl('')
  }

  const isUrlMatchOrIncluded = (str: string, url: string) => {
    // We have a special case with '/' (frontpage) as this url includes in every page URL i.e. '/calendar' OR '/profile'
    if (url !== '/') {
      return str.includes(url)
    } else {
      return str === url
    }
  }

  const findNavigation = (
    items: INavigation[] | INavigationHeader[],
    str: string,
    navOptions: INavigationOptions
  ) => {
    return items.find((item: INavigation | INavigationHeader) => {
      if (item && item.url && (isUrlMatchOrIncluded(str, item.url) || str === item.type)) {
        if (!navOptions.useNavigationItemInModern) {
          navOptions.useNavigationItemInModern = !!item.useModern
        }
        return true
      } else {
        return item?.type === str || (item?.url && isUrlMatchOrIncluded(str, item.url))
      }
    })
  }

  const navigationOptions = (str: string): INavigationOptions => {
    const navOptions: INavigationOptions = {
      checkLinkExistInNavigation: false,
      useNavigationItemInModern: false,
      navItem: undefined
    }
    // Find the page with the str we are looking for (str is either URL or a Document Type)
    // First we check the NavigationHeader
    let foundItem = findNavigation(navigation.value, str, navOptions)

    if (foundItem === undefined) {
      navigation.value.find((nav) => {
        if (nav.navigationItems !== undefined) {
          foundItem = findNavigation(nav.navigationItems, str, navOptions)
          return foundItem != undefined ? true : false
        } else {
          return false
        }
      })
    }

    if (foundItem) {
      navOptions.checkLinkExistInNavigation = true
      navOptions.navItem = foundItem
    }
    return navOptions
  }

  const setIsNavigating = (val: boolean) => {
    isNavigating.value = val
  }

  const setSecondaryNavigation = (nav: ISecondaryNavigationItem[]) => {
    secondaryNavigation.value = nav
  }

  return {
    navigation,
    currentNavigationUrl,
    navigationIsOpen,
    setNavigation,
    setSignInVisibility,
    isSignInVisible,
    setCurrentNavigationUrl,
    setNavigationIsOpen,
    resetNavigationStore,
    navigationOptions,
    isNavItemInMenu,
    setIsNavigating,
    isNavigating,
    setSecondaryNavigation,
    secondaryNavigation
  }
})
